import { Badge, Card, cn } from '@recipes/ui';
import { Clock, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { RecipePlaceholder } from '@/features/appearance/recipe-placeholder';

export interface RecipeCardData {
  id: string;
  title: string;
  coverThumbnailUrl: string | null;
  totalTimeMinutes: number | null;
  servings?: string;
  origin?: 'USER' | 'IMPORTED' | 'AI_GENERATED';
  sourceDomain?: string | null;
  /** For the coloured placeholder of a recipe without a photo. */
  mealType?: string | null;
  categories?: Array<{ id: string; slug?: string }>;
}

/**
 * Compact recipe tile. Feature-local duplicate of the core agent's RecipeCard
 * (src/components/RecipeCard) so collections/pantry/planning do not block on it.
 */
export function CompactRecipeCard({
  recipe,
  footer,
  actions,
  className,
  linkTo,
}: {
  recipe: RecipeCardData;
  footer?: ReactNode;
  actions?: ReactNode;
  className?: string;
  linkTo?: string;
}) {
  const { t } = useTranslation();
  return (
    <Card className={cn('flex h-full flex-col overflow-hidden', className)}>
      <Link
        to={linkTo ?? `/recipes/${recipe.id}`}
        className="block outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {recipe.coverThumbnailUrl ? (
          <img
            src={recipe.coverThumbnailUrl}
            alt=""
            className="aspect-[4/3] w-full object-cover"
            loading="lazy"
          />
        ) : (
          <RecipePlaceholder recipe={recipe} className="aspect-[4/3] w-full" />
        )}
        <div className="p-3">
          <h3 className="line-clamp-2 text-sm font-semibold">{recipe.title}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {recipe.totalTimeMinutes != null && (
              <span className="inline-flex items-center gap-1">
                <Clock className="size-3.5" aria-hidden />
                {recipe.totalTimeMinutes} {t('common.minutes')}
              </span>
            )}
            {recipe.servings && (
              <span className="inline-flex items-center gap-1">
                <Users className="size-3.5" aria-hidden />
                {recipe.servings}
              </span>
            )}
            {recipe.origin === 'IMPORTED' && recipe.sourceDomain && (
              <Badge variant="muted">{recipe.sourceDomain}</Badge>
            )}
            {recipe.origin === 'AI_GENERATED' && (
              <Badge variant="warning">{t('shared.recipeCard.ai')}</Badge>
            )}
          </div>
        </div>
      </Link>
      {footer && <div className="px-3 pb-2 text-xs">{footer}</div>}
      {actions && (
        <div className="mt-auto flex items-center justify-end gap-1 border-t p-1">{actions}</div>
      )}
    </Card>
  );
}
